import { useEffect, useState } from "react";
import { BottomSheet } from "@/ui/components";
import { Button } from "@/ui/components/Button";
import { Icon } from "@/ui/icons/Icon";
import { CardMastercardLogo, CardVisaLogo } from "@/assets/logos";
import type { BankCard } from "../../types/bankCard";
import styles from "./BankCardSelectSheet.module.scss";

const TITLE = "Cardul pe care primești banii";

interface BankCardSelectSheetProps {
  open: boolean;
  onClose: () => void;
  bankCards: BankCard[];
  selectedCardId: string | undefined;
  onSave: (bankCardId: string) => void;
  onAddCard: () => void;
  isAddingCard: boolean;
}

const getLastDigits = (cardMask: string) =>
  cardMask.replace(/\D/g, "").slice(-4);

/**
 * Picks the card the money is paid to, among the user's cards, or adds one
 * at the card partner.
 */
export function BankCardSelectSheet({
  open,
  onClose,
  bankCards,
  selectedCardId,
  onSave,
  onAddCard,
  isAddingCard,
}: BankCardSelectSheetProps) {
  const [checkedCardId, setCheckedCardId] = useState(selectedCardId);

  // Opens on the card in use, not on a choice left unsaved.
  useEffect(() => {
    if (open) {
      setCheckedCardId(selectedCardId);
    }
  }, [open, selectedCardId]);

  const handleSave = () => {
    if (checkedCardId) {
      onSave(checkedCardId);
    }
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={(next) => !next && onClose()}
      ariaLabel={TITLE}
      footer={
        <Button
          trackingId="bank_card_select_save"
          text="Salvează"
          disabled={!checkedCardId}
          onClick={handleSave}
          className={styles.saveButton}
        />
      }
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Închide"
          onClick={onClose}
        >
          <Icon name="times" size={20} />
        </button>
        <p className={styles.title} aria-hidden>
          {TITLE}
        </p>
      </div>

      <div className={styles.cards} role="radiogroup" aria-label={TITLE}>
        {bankCards.map((card) => {
          const BrandLogo = /visa/i.test(card.cardType)
            ? CardVisaLogo
            : CardMastercardLogo;
          const lastDigits = getLastDigits(card.cardMask);
          return (
            <label key={card.id} className={styles.card}>
              <span className={styles.brand} aria-hidden>
                <BrandLogo className={styles.brandLogo} />
              </span>
              <span className={styles.number}>
                <span className={styles.hiddenDigits} aria-hidden>
                  •••• •••• ••••
                </span>
                <span className={styles.visibleDigits}>{lastDigits}</span>
              </span>
              {card.isActive && <span className={styles.badge}>Principal</span>}
              <input
                type="radio"
                name="bank-card"
                className={styles.radio}
                value={card.id}
                checked={checkedCardId === card.id}
                onChange={() => setCheckedCardId(card.id)}
                aria-label={`Cardul terminat în ${lastDigits}`}
              />
            </label>
          );
        })}

        <Button
          trackingId="bank_card_select_add"
          variant="outlined"
          leftIcon={<Icon name="plus" size={16} />}
          loading={isAddingCard}
          onClick={onAddCard}
          className={styles.addButton}
          contentClassName={styles.addButtonText}
        >
          Adaugă card
        </Button>
      </div>
    </BottomSheet>
  );
}
